import { useState, useEffect } from "react";
import {
  designAiCompose, designAiScan, aiGetOpenAiKey, getAiPoints, fetchGallery,
  aiHelperStateGet, aiHelperStateSet, galleryImportUrl,
} from "@/lib/api";
import { sanitizeHtml } from "@/lib/sanitize";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Sparkles, Loader2, Wand2, ScanSearch, CheckCircle2, XCircle, ShieldAlert, ArrowLeft, RefreshCw, Check, X, Images, Undo2, Plus,
  History, Download,
} from "lucide-react";

const IMAGE_TAGS = ["logo", "hero", "owner", "team", "interior", "storefront", "treatment", "product", "before-after", "other"];
import { toast } from "sonner";

const KIND_TITLES = { about: "About Us", bio: "Brand bio", home: "Home page", email: "content" };
const KIND_PLACEHOLDERS = {
  about: "e.g. 20+ years in skincare, family-run, focus on clean peptides…",
  home: "e.g. welcome visitors, highlight my spa services + OreVital peptides, invite them to shop…",
  bio: "e.g. licensed esthetician, loves helping clients glow, based in Orlando",
};

function aiErrorMessage(err, fallback) {
  if (err?.response?.data?.detail) return err.response.data.detail;
  if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) return "The AI took too long to answer — please try again (long pages take longer to read).";
  if (!err?.response) return "Lost connection while the AI was working — check your internet and try again.";
  return fallback;
}

function hostOf(u) { try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return u; } }
function pathOf(u) { try { const p = new URL(u).pathname.replace(/\/$/, ""); return p || "/"; } catch { return u; } }

// Reusable "AI helper" for text/HTML fields. kind: "bio" | "about" | "home" | "email".
export default function AiHelperDialog({ userRecordId, kind = "bio", existing = "", onResult, label = "AI helper" }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [urls, setUrls] = useState(["", "", ""]);
  const [provider, setProvider] = useState("emergent");
  const [openaiConnected, setOpenaiConnected] = useState(false);
  const [points, setPoints] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState(null); // { scan_id, sources:[{url, ok, blocked, title, chars, excerpt, images, parts:[{url,label,chars}]}] }
  const [selected, setSelected] = useState({}); // source url -> bool
  const [excludedPages, setExcludedPages] = useState({}); // sub-page url -> true (excluded)
  const [preview, setPreview] = useState(null); // { value, sources }
  const [omitted, setOmitted] = useState({}); // image url -> true
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [gallery, setGallery] = useState(null); // [{key,url}]
  const [extraImages, setExtraImages] = useState([]); // picked from gallery
  const [tags, setTags] = useState({}); // image url -> tag
  const [restored, setRestored] = useState(null); // ISO date of restored state
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!open) return;
    aiGetOpenAiKey(userRecordId)
      .then((r) => { setOpenaiConnected(!!r.connected); if (r.default_provider) setProvider(r.default_provider); })
      .catch(() => {});
    getAiPoints(userRecordId).then((r) => setPoints(r)).catch(() => {});
    if (kind !== "email") {
      aiHelperStateGet(userRecordId, kind).then((r) => {
        const st = r.state;
        if (!st || !Array.isArray(st.urls)) return;
        setUrls([0, 1, 2].map((i) => st.urls[i] || ""));
        if (st.scan?.scan_id && Array.isArray(st.scan?.sources)) setScan(st.scan);
        setSelected(st.selected || {});
        setExcludedPages(st.excludedPages || {});
        setOmitted(st.omitted || {});
        setExtraImages(Array.isArray(st.extraImages) ? st.extraImages : []);
        setTags(st.tags || {});
        setRestored(r.updated_at || "saved");
      }).catch(() => {});
    }
  }, [open, userRecordId, kind]);

  function persist(over = {}) {
    if (kind === "email") return;
    const st = {
      urls, scan: scan ? { scan_id: scan.scan_id, sources: scan.sources } : null,
      selected, excludedPages, omitted, extraImages, tags, ...over,
    };
    aiHelperStateSet(userRecordId, kind, st).catch(() => {});
  }

  function clearSaved() {
    reset();
    setRestored(null);
    aiHelperStateSet(userRecordId, kind, null).catch(() => {});
    toast.success("Saved scan cleared");
  }

  const cleanUrls = urls.map((u) => u.trim()).filter(Boolean);
  const scanStale = scan && (scan.sources.map((s) => s.url).join("|") !== cleanUrls.join("|"));
  const activeUrls = scan && !scanStale ? cleanUrls.filter((u) => selected[u]) : cleanUrls;
  const cost = 1 + activeUrls.length;
  const scannedImages = scan && !scanStale
    ? [...new Set(scan.sources.filter((s) => s.ok && selected[s.url]).flatMap((s) => s.images || []))]
    : [];
  const candidateImages = [...new Set([...scannedImages, ...extraImages])];
  const chosenImages = candidateImages.filter((u) => !omitted[u]);

  function reset() {
    setNotes(""); setUrls(["", "", ""]); setScan(null); setSelected({}); setExcludedPages({}); setPreview(null);
    setOmitted({}); setExtraImages([]); setGalleryOpen(false); setTags({}); setImportUrl("");
  }

  function close() {
    if (scan && !scanStale) persist();
    setOpen(false);
    setPreview(null); setNotes(""); setGalleryOpen(false); setImportUrl("");
  }

  async function runScan() {
    if (cleanUrls.length === 0) { toast.error("Paste at least one link to scan"); return; }
    setScanning(true);
    try {
      const res = await designAiScan(userRecordId, cleanUrls);
      setScan(res);
      const sel = {};
      res.sources.forEach((s) => { sel[s.url] = !!s.ok; });
      setSelected(sel);
      setExcludedPages({});
      setOmitted({});
      const firstImg = res.sources.find((x) => x.ok && x.images?.length)?.images?.[0];
      const t = firstImg ? { [firstImg]: "hero" } : {};
      setTags(t);
      setRestored(null);
      persist({ scan: { scan_id: res.scan_id, sources: res.sources }, selected: sel, excludedPages: {}, omitted: {}, tags: t });
      const okCount = res.sources.filter((s) => s.ok).length;
      if (okCount === 0) toast.error("Nothing readable found in those links — paste the text into the notes instead");
      else toast.success(`Scanned ${okCount} of ${res.sources.length} link${res.sources.length > 1 ? "s" : ""} — review below`);
    } catch (err) {
      toast.error(aiErrorMessage(err, "Could not scan links — please try again"));
    } finally {
      setScanning(false);
    }
  }

  async function generate() {
    if (!notes.trim() && activeUrls.length === 0 && !existing) {
      toast.error("Add a few notes or a reference link first");
      return;
    }
    const bal = points?.balance ?? 0;
    if (bal < cost) { toast.error(`Not enough AI points — needs ${cost}, you have ${bal}. You earn more as your sales grow.`); return; }
    if (!window.confirm(`This will use ${cost} AI point${cost > 1 ? "s" : ""}. You have ${bal}. Continue?`)) return;
    setBusy(true);
    try {
      const res = await designAiCompose({
        user_record_id: userRecordId, content: notes, urls: activeUrls,
        scan_id: scan && !scanStale ? scan.scan_id : undefined,
        excluded_pages: Object.keys(excludedPages).filter((k) => excludedPages[k]),
        images: candidateImages.length ? chosenImages : undefined,
        image_tags: Object.fromEntries(chosenImages.filter((u) => tags[u]).map((u) => [u, tags[u]])),
        provider, kind, existing: existing || "",
      });
      const value = kind === "bio" ? (res.text || "") : (res.body_html || res.text || "");
      if (res.points) setPoints(res.points);
      const srcs = res.sources || [];
      const failed = srcs.filter((s) => !s.ok);
      if (failed.length) {
        toast.warning(
          `Could not read: ${failed.map((s) => hostOf(s.url)).join(", ")}${failed.some((s) => s.blocked) ? " (blocks automated readers — paste the text instead)" : ""}`,
          { duration: 8000 },
        );
      }
      setPreview({ value, sources: srcs });
      if (scan && !scanStale) persist();
    } catch (err) {
      toast.error(aiErrorMessage(err, "The AI hit a snag — please try again"), { duration: 7000 });
    } finally {
      setBusy(false);
    }
  }

  async function openGallery() {
    setGalleryOpen((v) => !v);
    if (gallery === null) {
      try {
        const r = await fetchGallery(userRecordId);
        setGallery((r.items || []).filter((it) => /\.(png|jpe?g|webp)$/i.test(it.key || it.url || "")));
      } catch {
        setGallery([]);
      }
    }
  }

  async function importFromUrl() {
    const u = importUrl.trim();
    if (!u.startsWith("http")) { toast.error("Paste an image link or a post link first"); return; }
    setImporting(true);
    try {
      const r = await galleryImportUrl(userRecordId, u);
      setGallery((g) => [{ key: r.key, url: r.url }, ...(g || [])]);
      setExtraImages((prev) => prev.includes(r.url) ? prev : [...prev, r.url]);
      setImportUrl("");
      toast.success("Photo saved to your Gallery and added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not import that link", { duration: 9000 });
    } finally {
      setImporting(false);
    }
  }

  function apply() {
    onResult?.(preview.value);
    toast.success(`${KIND_TITLES[kind] || "Content"} updated — remember to Save`);
    close();
  }

  const title = KIND_TITLES[kind] || "content";

  return (
    <>
      <Button type="button" variant="outline" size="sm"
        onClick={() => setOpen(true)}
        className="rounded-lg h-8 border-violet-200 text-violet-700 hover:bg-violet-50"
        data-testid={`ai-helper-${kind}-btn`}>
        <Sparkles size={13} className="mr-1" /> {label}
      </Button>

      <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else setOpen(true); }}>
        <DialogContent className={`${preview ? "max-w-2xl" : "max-w-lg"} max-h-[92vh] overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)]`} data-testid={`ai-helper-${kind}-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 size={18} className="text-violet-600" />
              {preview ? `Preview — ${title}` : `AI helper — ${title}`}
            </DialogTitle>
          </DialogHeader>

          {preview ? (
            <div className="space-y-3" data-testid={`ai-helper-${kind}-preview`}>
              <p className="text-xs text-zinc-500">
                Review the draft below. Use it to replace your current {title.toLowerCase()}, go back to tweak your notes, or regenerate.
              </p>
              {kind === "bio" ? (
                <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-800 whitespace-pre-wrap" data-testid={`ai-helper-${kind}-preview-text`}>
                  {preview.value}
                </div>
              ) : (
                <div className="rounded-xl border border-zinc-200 bg-white p-3 max-h-[52vh] overflow-auto" data-testid={`ai-helper-${kind}-preview-html`}>
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(preview.value) }} />
                </div>
              )}
              {preview.sources?.some((s) => s.ok) && (
                <div className="text-[11px] text-zinc-500">
                  Based on: {preview.sources.filter((s) => s.ok).map((s) => s.title || hostOf(s.url)).join(" · ")}
                </div>
              )}
            </div>
          ) : (
          <div className="space-y-3">
            {restored && scan && (
              <div className="flex items-center gap-2 rounded-lg bg-violet-50 border border-violet-100 px-3 py-2 text-xs text-violet-800" data-testid={`ai-helper-${kind}-restored`}>
                <History size={13} className="shrink-0" />
                <span className="flex-1 min-w-0 truncate">
                  Restored your last scan ({scan.sources.map((x) => hostOf(x.url)).join(", ")}
                  {restored !== "saved" ? ` · ${new Date(restored).toLocaleDateString()}` : ""}) — just hit {existing ? "Rewrite" : "Generate"}.
                </span>
                <button type="button" onClick={clearSaved} className="shrink-0 underline hover:text-violet-950" data-testid={`ai-helper-${kind}-clear-saved`}>Clear</button>
              </div>
            )}
            <div>
              <div className="text-xs font-medium text-zinc-600 mb-1">
                {existing ? "What should I change or improve?" : "Notes / rough idea"}
              </div>
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder={KIND_PLACEHOLDERS[kind] || KIND_PLACEHOLDERS.bio}
                className="rounded-lg text-sm" data-testid={`ai-helper-${kind}-notes`} />
            </div>
            <div>
              <div className="text-xs font-medium text-zinc-600 mb-1">
                Reference links (optional, up to 3) — paste your website, Google Doc, or dealer page
                (e.g. orevital.com/d/you). AI scans them for content &amp; images. Instagram/Facebook can't be
                read automatically — upload those photos to your Gallery and add them below.
              </div>
              {urls.map((u, i) => (
                <Input key={i} value={u}
                  onChange={(e) => setUrls((prev) => prev.map((x, j) => j === i ? e.target.value : x))}
                  placeholder={`https://… ${i + 1}`} className="h-9 rounded-lg text-sm mb-1.5"
                  data-testid={`ai-helper-${kind}-url-${i}`} />
              ))}
              <Button type="button" variant="outline" size="sm" onClick={runScan}
                disabled={scanning || cleanUrls.length === 0}
                className="h-8 rounded-lg mt-0.5"
                data-testid={`ai-helper-${kind}-scan`}>
                {scanning ? <Loader2 className="animate-spin mr-1" size={13} /> : <ScanSearch size={13} className="mr-1" />}
                {scan && !scanStale ? "Re-scan links" : "Scan links (free)"}
              </Button>
            </div>

            {scan && !scanStale && (
              <div className="space-y-2" data-testid={`ai-helper-${kind}-scan-results`}>
                <div className="text-xs font-medium text-zinc-600">What the AI will use — untick anything that looks wrong</div>
                {scan.sources.map((s, i) => (
                  <div key={s.url}
                    className={`rounded-xl border p-2.5 ${s.ok ? "border-zinc-200 bg-white" : "border-rose-200 bg-rose-50/60"}`}
                    data-testid={`ai-helper-${kind}-source-${i}`}>
                    <label className={`flex gap-2.5 ${s.ok ? "cursor-pointer" : "cursor-not-allowed"}`}>
                      <input type="checkbox" className="mt-1 accent-violet-600" disabled={!s.ok}
                        checked={!!selected[s.url]}
                        onChange={(e) => setSelected((prev) => ({ ...prev, [s.url]: e.target.checked }))}
                        data-testid={`ai-helper-${kind}-source-${i}-toggle`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 text-xs">
                          {s.ok ? <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />
                            : s.blocked ? <ShieldAlert size={13} className="text-rose-600 shrink-0" />
                            : <XCircle size={13} className="text-rose-600 shrink-0" />}
                          <span className="font-semibold text-zinc-800 truncate">{s.title || hostOf(s.url)}</span>
                          <span className="text-zinc-400 shrink-0">· {hostOf(s.url)}</span>
                          {s.ok && <span className="text-zinc-400 shrink-0">· {s.chars.toLocaleString()} chars</span>}
                        </div>
                        <p className="text-[11px] text-zinc-600 leading-snug mt-1 line-clamp-3">
                          {s.ok ? s.excerpt : s.blocked
                            ? (s.reason || "This site blocks automated readers — copy its text into the notes box instead.")
                            : "Nothing readable found at this link."}
                        </p>
                      </div>
                    </label>
                    {s.ok && selected[s.url] && s.parts?.length > 1 && (
                      <div className="mt-2 ml-6 border-t border-zinc-100 pt-2" data-testid={`ai-helper-${kind}-source-${i}-pages`}>
                        <div className="text-[11px] font-medium text-zinc-500 mb-1">Pages to include from this site</div>
                        <div className="flex flex-wrap gap-1.5">
                          {s.parts.map((p, j) => {
                            const isMain = j === 0;
                            const on = isMain || !excludedPages[p.url];
                            return (
                              <label key={p.url}
                                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] ${isMain ? "border-violet-200 bg-violet-50 text-violet-800" : on ? "border-zinc-300 bg-white text-zinc-800 cursor-pointer" : "border-zinc-200 bg-zinc-50 text-zinc-400 line-through cursor-pointer"}`}
                                title={p.url}
                                data-testid={`ai-helper-${kind}-source-${i}-page-${j}`}>
                                <input type="checkbox" className="accent-violet-600 h-3 w-3" checked={on} disabled={isMain}
                                  onChange={(e) => setExcludedPages((prev) => ({ ...prev, [p.url]: !e.target.checked }))}
                                  data-testid={`ai-helper-${kind}-source-${i}-page-${j}-toggle`} />
                                <span className="font-medium">{isMain ? "Main page" : p.label}</span>
                                <span className="opacity-60">{pathOf(p.url)} · {Math.round(p.chars / 100) / 10}k</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {kind !== "bio" && (
              <div className="space-y-2" data-testid={`ai-helper-${kind}-images`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-medium text-zinc-600">
                    Images the AI may use <span className="text-zinc-400 font-normal">· {chosenImages.length} of {candidateImages.length} kept — × to omit, tag to place (hero, owner, storefront…)</span>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={openGallery} className="h-7 rounded-lg text-xs"
                    data-testid={`ai-helper-${kind}-gallery-toggle`}>
                    <Images size={12} className="mr-1" /> {galleryOpen ? "Hide my Gallery" : "Add from my Gallery"}
                  </Button>
                </div>
                {candidateImages.length === 0 && !galleryOpen && (
                  <p className="text-[11px] text-zinc-400">Decorate the page with your own photos — pick them from your Gallery (storefront, team, treatment room…). Scanned websites add their images here too.</p>
                )}
                {candidateImages.length > 0 && (
                  <div className="flex flex-wrap gap-2" data-testid={`ai-helper-${kind}-image-grid`}>
                    {candidateImages.map((im, i) => {
                      const off = !!omitted[im];
                      return (
                        <div key={im} className="flex flex-col items-center gap-1 w-16" data-testid={`ai-helper-${kind}-image-${i}`} data-omitted={off ? "1" : "0"}>
                        <div className={`relative h-16 w-16 rounded-lg overflow-hidden border ${off ? "border-zinc-200 opacity-40" : "border-violet-300 ring-2 ring-violet-100"}`}>
                          <img src={im} alt="" className="h-full w-full object-cover bg-white"
                            onError={(e) => { e.currentTarget.parentElement.parentElement.style.display = "none"; }} />
                          <button type="button"
                            onClick={() => setOmitted((prev) => ({ ...prev, [im]: !off }))}
                            className={`absolute top-0.5 right-0.5 h-5 w-5 rounded-full flex items-center justify-center shadow ${off ? "bg-emerald-600 text-white" : "bg-white/95 text-zinc-700 hover:bg-rose-600 hover:text-white"}`}
                            aria-label={off ? "Keep this image" : "Omit this image"}
                            title={off ? "Keep this image" : "Omit this image"}
                            data-testid={`ai-helper-${kind}-image-${i}-toggle`}>
                            {off ? <Undo2 size={11} /> : <X size={11} />}
                          </button>
                        </div>
                        {!off && (
                          <select value={tags[im] || ""} onChange={(e) => setTags((prev) => ({ ...prev, [im]: e.target.value }))}
                            className={`w-16 h-5 rounded text-[10px] border px-0.5 ${tags[im] ? "border-violet-300 text-violet-800 bg-violet-50" : "border-zinc-200 text-zinc-500 bg-white"}`}
                            aria-label="Image role" data-testid={`ai-helper-${kind}-image-${i}-tag`}>
                            <option value="">tag…</option>
                            {IMAGE_TAGS.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                        )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {galleryOpen && (
                  <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-2 space-y-2" data-testid={`ai-helper-${kind}-gallery`}>
                    <div className="flex items-center gap-1.5">
                      <Input value={importUrl} onChange={(e) => setImportUrl(e.target.value)}
                        placeholder="Paste an image link (Instagram: right-click photo → Copy image address)…"
                        className="h-8 rounded-lg text-xs bg-white" data-testid={`ai-helper-${kind}-import-url`} />
                      <Button type="button" size="sm" onClick={importFromUrl} disabled={importing || !importUrl.trim()}
                        className="h-8 rounded-lg text-xs bg-violet-600 hover:bg-violet-700 text-white shrink-0"
                        data-testid={`ai-helper-${kind}-import-btn`}>
                        {importing ? <Loader2 className="animate-spin" size={12} /> : <><Download size={12} className="mr-1" /> Save to Gallery</>}
                      </Button>
                    </div>
                    {gallery === null ? (
                      <Loader2 className="animate-spin text-zinc-400" size={14} />
                    ) : gallery.length === 0 ? (
                      <p className="text-[11px] text-zinc-500">Your Gallery is empty — upload photos (e.g. from Instagram) in the Gallery tab first.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                        {gallery.slice(0, 120).map((it, i) => {
                          const on = extraImages.includes(it.url);
                          return (
                            <button key={it.key || it.url} type="button"
                              onClick={() => setExtraImages((prev) => on ? prev.filter((u) => u !== it.url) : [...prev, it.url])}
                              className={`relative h-14 w-14 rounded-md overflow-hidden border ${on ? "border-emerald-500 ring-2 ring-emerald-200" : "border-zinc-200 hover:border-violet-300"}`}
                              title={it.key}
                              data-testid={`ai-helper-${kind}-gallery-item-${i}`}>
                              <img src={it.url} alt="" loading="lazy" className="h-full w-full object-cover bg-white"
                                onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                              <span className={`absolute bottom-0.5 right-0.5 h-4 w-4 rounded-full flex items-center justify-center text-white ${on ? "bg-emerald-600" : "bg-zinc-700/70"}`}>
                                {on ? <Check size={10} /> : <Plus size={10} />}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {openaiConnected && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-zinc-500">AI engine:</span>
                <div className="flex rounded-lg bg-zinc-100 p-0.5">
                  <button type="button" onClick={() => setProvider("emergent")}
                    className={`px-2.5 h-7 rounded-md font-medium ${provider === "emergent" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500"}`}
                    data-testid={`ai-helper-${kind}-provider-emergent`}>OreVital AI</button>
                  <button type="button" onClick={() => setProvider("openai")}
                    className={`px-2.5 h-7 rounded-md font-medium ${provider === "openai" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500"}`}
                    data-testid={`ai-helper-${kind}-provider-openai`}>OpenAI (GPT-5.6)</button>
                </div>
              </div>
            )}
          </div>
          )}

          <DialogFooter className="flex-wrap gap-2">
            {points && (
              <span className="text-xs text-zinc-500 mr-auto self-center" data-testid={`ai-helper-${kind}-points`}>
                <Sparkles size={11} className="inline mb-0.5 text-amber-500" /> <strong className="text-zinc-700">{points.balance}</strong> AI points · uses {cost}
              </span>
            )}
            {preview ? (
              <>
                <Button variant="ghost" onClick={() => setPreview(null)} data-testid={`ai-helper-${kind}-preview-back`}>
                  <ArrowLeft size={14} className="mr-1" /> Back
                </Button>
                <Button variant="outline" onClick={generate} disabled={busy} data-testid={`ai-helper-${kind}-preview-regenerate`}>
                  {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : <RefreshCw size={14} className="mr-1" />} Regenerate
                </Button>
                <Button onClick={apply} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`ai-helper-${kind}-preview-apply`}>
                  <Check size={15} className="mr-1" /> Use this
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={close} data-testid={`ai-helper-${kind}-cancel`}>Cancel</Button>
                <Button onClick={generate} disabled={busy}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid={`ai-helper-${kind}-generate`}>
                  {busy ? <Loader2 className="animate-spin mr-1" size={15} /> : <Sparkles size={15} className="mr-1" />}
                  {existing ? "Rewrite" : "Generate"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
